/**
 * CouponHeroBlock — the visual centerpiece for a coupon-based Pricing Rule,
 * the counterpart to the Customer page's "Rewards" card + action buttons.
 *
 *   • Ticket   — the reward + the redeemable code, styled like a real coupon
 *   • Actions  — คัดลอกโค้ด / แชร์ลิงก์ / จัดการ (copy code, copy share link, manage)
 *   • Metrics  — ใช้แล้ว / เก็บแล้ว (redeemed & collected counts, from the coupon doc)
 *
 * All from the already-loaded doc + linked coupon — no extra endpoint.
 */

import { Link } from "react-router-dom";
import { Copy, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "@/lib/store/authStore";
import { rewardSummary, type Lang } from "./pricingRuleDetailModel";
import { DuplicatePromoButton } from "./DuplicatePromoButton";
import type { LinkedCoupon } from "./usePricingRuleCoupons";

const pick = (lang: Lang, th: string, en: string) => (lang === "th" ? th : en);

async function copy(text: string, okMsg: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    toast.error("คัดลอกไม่สำเร็จ");
  }
}

/**
 * A single stat tile. When a cap exists (`cap > 0`) it becomes a mini progress
 * meter: a slim bar (used / cap) plus a "X left · Y% used" caption, coloured
 * emerald → amber → red as it nears the cap so a nearly-exhausted coupon is
 * obvious at a glance. With no cap it stays a plain count ("12  Unlimited").
 * Pure presentation over data already loaded on the coupon doc.
 */
function MetricTile({
  label,
  value,
  cap,
  unlimitedLabel,
  leftLabel,
  usedPctLabel,
}: {
  label: string;
  value: number;
  cap: number;
  unlimitedLabel: string;
  leftLabel: (left: number) => string;
  usedPctLabel: (pct: number) => string;
}) {
  const capped = cap > 0;
  const pct = capped ? Math.min(100, Math.round((value / cap) * 100)) : 0;
  const left = capped ? Math.max(0, cap - value) : 0;

  // emerald (plenty) → amber (running low) → red (nearly / fully used)
  const barColor =
    pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  const capTextColor =
    pct >= 90
      ? "text-red-600 dark:text-red-400"
      : pct >= 70
        ? "text-amber-600 dark:text-amber-400"
        : "text-emerald-600 dark:text-emerald-400";

  return (
    <div className="rounded-xl bg-muted/40 px-4 py-3 dark:bg-white/[0.03]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-[20px] font-bold leading-none tabular-nums text-gray-900 dark:text-gray-100">{value}</span>
        <span className="text-[12px] text-gray-400 dark:text-gray-500">
          {capped ? `/ ${cap}` : unlimitedLabel}
        </span>
      </div>

      {capped ? (
        <div className="mt-2.5 space-y-1">
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
            <div
              className={`h-full rounded-full transition-all ${barColor}`}
              style={{ width: `${Math.max(pct, value > 0 ? 3 : 0)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px] tabular-nums">
            <span className={`font-semibold ${capTextColor}`}>{leftLabel(left)}</span>
            <span className="text-gray-400 dark:text-gray-500">{usedPctLabel(pct)}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ActionButton({
  onClick,
  to,
  icon: Icon,
  children,
}: {
  onClick?: () => void;
  to?: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  const cls =
    "inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[13px] font-medium text-gray-700 transition-colors hover:border-sky-300 hover:text-sky-700 dark:text-gray-200 dark:hover:border-sky-500/40 dark:hover:text-sky-400";
  const inner = (
    <>
      <Icon className="h-3.5 w-3.5" />
      {children}
    </>
  );
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

export function CouponHeroBlock({
  formData,
  coupon,
  lang,
}: {
  formData: Record<string, any>;
  coupon: LinkedCoupon;
  lang: Lang;
}) {
  const tenant = useAuthStore((s) => s.currentTenant);
  const code = coupon.coupon_code;
  const shareUrl = tenant ? `https://${tenant}.shop.zaviago.com/coupons?code=${encodeURIComponent(code)}` : "";

  const used = Number(coupon.used) || 0;
  const maxUse = Number(coupon.maximum_use) || 0;
  const collected = Number(coupon.current_collections) || 0;
  const maxCollect = Number(coupon.max_collections) || 0;
  const unlimited = pick(lang, "ไม่จำกัด", "Unlimited");

  return (
    <div className="space-y-3">
      {/* Ticket */}
      <div className="relative flex overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-sky-50 to-white dark:from-sky-500/[0.07] dark:to-transparent">
        {/* Reward side */}
        <div className="flex-1 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {pick(lang, "สิ่งที่ลูกค้าจะได้รับ", "What customers get")}
          </div>
          <div className="mt-1 text-[22px] font-bold leading-tight text-gray-900 dark:text-gray-100 break-words">
            {rewardSummary(formData, lang)}
          </div>
        </div>

        {/* Perforation */}
        <div className="relative w-px shrink-0 border-l border-dashed border-sky-300/70 dark:border-sky-500/30">
          <span className="absolute -left-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-background" />
          <span className="absolute -left-[7px] -bottom-[7px] h-3.5 w-3.5 rounded-full bg-background" />
        </div>

        {/* Code side */}
        <div className="flex w-[42%] max-w-[200px] shrink-0 flex-col justify-center gap-1 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {pick(lang, "โค้ด", "Code")}
          </div>
          <button
            type="button"
            onClick={() => copy(code, pick(lang, "คัดลอกโค้ดแล้ว", "Code copied"))}
            className="group inline-flex items-center gap-2 text-left"
            title={pick(lang, "คัดลอกโค้ด", "Copy code")}
          >
            <span className="truncate font-mono text-[18px] font-bold tracking-wide text-sky-700 dark:text-sky-300">
              {code}
            </span>
            <Copy className="h-3.5 w-3.5 shrink-0 text-sky-400 transition-colors group-hover:text-sky-600 dark:group-hover:text-sky-300" />
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <ActionButton icon={Copy} onClick={() => copy(code, pick(lang, "คัดลอกโค้ดแล้ว", "Code copied"))}>
          {pick(lang, "คัดลอกโค้ด", "Copy code")}
        </ActionButton>
        {shareUrl && (
          <ActionButton icon={Share2} onClick={() => copy(shareUrl, pick(lang, "คัดลอกลิงก์แล้ว", "Link copied"))}>
            {pick(lang, "แชร์ลิงก์", "Share link")}
          </ActionButton>
        )}
        <ActionButton icon={ExternalLink} to={`/promotions/${encodeURIComponent(code)}`}>
          {pick(lang, "จัดการ", "Manage")}
        </ActionButton>
        <DuplicatePromoButton formData={formData} lang={lang} />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-2.5">
        <MetricTile
          label={pick(lang, "ใช้แล้ว", "Redeemed")}
          value={used}
          cap={maxUse}
          unlimitedLabel={unlimited}
          leftLabel={(left) => pick(lang, `เหลือ ${left}`, `${left} left`)}
          usedPctLabel={(pct) => pick(lang, `ใช้ไป ${pct}%`, `${pct}% used`)}
        />
        <MetricTile
          label={pick(lang, "เก็บแล้ว", "Collected")}
          value={collected}
          cap={maxCollect}
          unlimitedLabel={unlimited}
          leftLabel={(left) => pick(lang, `เหลือ ${left}`, `${left} left`)}
          usedPctLabel={(pct) => pick(lang, `เก็บไป ${pct}%`, `${pct}% claimed`)}
        />
      </div>
    </div>
  );
}
